import * as d3 from "d3";
import React, { useEffect, useRef, useState, type CSSProperties } from "react";

import {
  ChartSeriesType,
  DateTimelineProps,
} from "@/components/organisms/DateTimeline/DateTimeline.types";

interface DateTimelineTooltipPropsType<T> {
  svgRef: React.RefObject<SVGSVGElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  margin: { top: number; right: number; bottom: number; left: number };
  chartHeight: number;
  width: number;
  data: T[];
  dateKey: keyof T;
  adjustedXScale: d3.ScaleBand<Date>;
  tooltip: NonNullable<DateTimelineProps<T>["tooltip"]>;
  series: ChartSeriesType[];
}

export function DateTimelineTooltip<T>({
  svgRef,
  containerRef,
  margin,
  chartHeight,
  width,
  data,
  dateKey,
  adjustedXScale,
  tooltip,
  series,
}: DateTimelineTooltipPropsType<T>) {
  const [tooltipData, setTooltipData] = useState<{
    content: React.ReactNode;
    x: number;
    y: number;
    transform: string;
  } | null>(null);
  const tooltipHideTimer = useRef<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef(tooltip);
  useEffect(() => {
    tooltipRef.current = tooltip;
  }, [tooltip]);

  // Handle tooltip logic
  useEffect(() => {
    const svgSelection = d3.select(svgRef.current);

    if (tooltipRef.current) {
      const currentTooltip = tooltipRef.current;
      svgSelection
        .on("mousemove", function (event) {
          if (tooltipHideTimer.current) {
            clearTimeout(tooltipHideTimer.current);
            tooltipHideTimer.current = null;
          }
          const [x, y] = d3.pointer(event);

          // Check if cursor is within the main chart's bounds
          const isInChartBounds =
            x >= margin.left &&
            x <= width - margin.right &&
            y >= margin.top &&
            y <= margin.top + chartHeight;

          if (!isInChartBounds) {
            setTooltipData(null);
            return;
          }

          // Adjust x-coordinate to be relative to the chart area for bisecting
          const chartX = x - margin.left;

          // Create an array of the start of each band to use for bisecting
          const domain = adjustedXScale.domain();
          const range = adjustedXScale.range();
          const step = adjustedXScale.step();
          const rangePoints = Array.from(
            { length: domain.length },
            (_, i) => range[0] + i * step,
          );

          // Find the index of the band. bisectRight returns the insertion point.
          let i = d3.bisectRight(rangePoints, chartX);
          // We want the band to the left of the cursor, so we decrement.
          if (i > 0) {
            i--;
          }

          const dataPoint = data[i];
          if (!dataPoint) {
            tooltipHideTimer.current = setTimeout(() => {
              setTooltipData(null);
            }, 300);
            return;
          }

          const bandStart = adjustedXScale(dataPoint[dateKey] as Date);

          if (bandStart === undefined) {
            tooltipHideTimer.current = setTimeout(() => {
              setTooltipData(null);
            }, 300);
            return;
          }
          const bandEnd = bandStart + adjustedXScale.bandwidth();

          // Check if pointer is within the band's bounds (not in the padding)
          if (chartX >= bandStart && chartX <= bandEnd) {
            const tooltipContent = currentTooltip(dataPoint, series);
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const tooltipX =
              margin.left + bandStart + adjustedXScale.bandwidth() / 2;
            const clientY = event.clientY - rect.top;
            const chartCenterX = width / 2;
            const chartCenterY = margin.top + chartHeight / 2;
            let translateX: string;
            let translateY: string;
            if (tooltipX > chartCenterX) {
              translateX = "calc(-100% - 10px)";
            } else {
              translateX = "10px";
            }
            if (clientY > chartCenterY) {
              translateY = "calc(-100% - 10px)";
            } else {
              translateY = "10px";
            }
            setTooltipData({
              content: tooltipContent,
              x: tooltipX,
              y: clientY,
              transform: `translate(${translateX}, ${translateY})`,
            });
          } else {
            tooltipHideTimer.current = setTimeout(() => {
              setTooltipData(null);
            }, 300);
          }
        })
        .on("mouseleave", function () {
          setTooltipData(null);
        });
    } else {
      svgSelection.on("mousemove", null).on("mouseleave", null);
    }

    return () => {
      svgSelection.on("mousemove", null).on("mouseleave", null);
    };
  }, [
    width,
    chartHeight,
    margin,
    data,
    dateKey,
    adjustedXScale,
    svgRef,
    containerRef,
    series,
  ]);

  if (!tooltipData) return null;

  return (
    <div
      className="absolute top-(--y-2) left-(--x-2) transform-(--transform) z-10 [transition:opacity_0.2s,left_0.2s,transform_0.2s] pointer-events-none"
      style={
        {
          "--y-2": `${tooltipData.y}px`,
          "--x-2": `${tooltipData.x}px`,
          "--transform": tooltipData.transform,
        } as CSSProperties
      }
    >
      {tooltipData.content}
    </div>
  );
}
